<template>
  <div class="flex flex-col w-full h-full text-white">
    <ul>
      <li v-for="({ title, path, icon }, index) in lists" :key="index">
        <router-link
          :to="{ path: path, query: $route.query }"
          class="block capitalize hover:bg-gray-800 p-3 pl-10"
          exact-active-class="md:bg-gray-900 bg-gray-800"
        >
          <i :class="icon" class="mr-3" />
          {{ title }}
        </router-link>
      </li>
    </ul>
    <!-- Filter -->
    <div class="border-t flex-1 border-gray-600 my-3 text-xs">
      <div class="uppercase p-3 bg-secondary">
        <p class="font-semibold">Browse options</p>
      </div>
      <perfect-scrollbar :options="{ suppressScrollX: true }">
        <div class="text-gray-300" style="max-height: calc(100vh - 300px)">
          <div class="border-b border-gray-600 mb-2 p-3">
            <p class="uppercase">Released</p>
            <div class="flex items-center my-2">
              <p class="w-8">from:</p>
              <datepicker v-model="selected.from" :lowerLimit="limit_date" />
            </div>
            <div class="flex items-center my-2">
              <p class="w-8">to:</p>
              <datepicker v-model="selected.to" />
            </div>
          </div>
          <div class="border-b border-gray-600 mb-2 p-3">
            <p class="uppercase">genre</p>
            <select class="select-css" v-model="genre">
              <option
                :value="genre.id"
                v-for="genre in genresOption"
                :key="genre.id"
                >{{ genre.name }}</option
              >
            </select>
          </div>
          <div class="border-b border-gray-600 mb-2 p-3">
            <p class="uppercase">within runtime</p>
            <no-ui-slider
              v-bind="{
                range: runtime['range'],
                id: sliderIDs.runtime,
                start: Array.from(runtime['start'])
              }"
            />
          </div>
        </div>
      </perfect-scrollbar>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted } from "vue";
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import NoUiSlider from "@/components/RangeSlider/NoUiSlider.vue";
import Datepicker from "vue3-datepicker";
import { add } from "date-fns";
import moment from "moment";
export default {
  name: "browse-and-filter",
  components: { NoUiSlider, Datepicker },
  props: ["sliderIDs"],
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();
    const formatSelectedDate = date => moment(date).format("YYYY-MM-DD");
    let movie_store = localStorage.getItem("movie_store");
    if (movie_store) {
      movie_store = JSON.parse(movie_store);
    }
    let limit_date = ref(null);
    let genre = ref(movie_store.genre || null);
    let selected = ref({
      from: limit_date,
      to: add(new Date(), { months: 8 })
    });
    const runtime = ref({
      start: movie_store.runtime,
      range: {
        min: 1,
        max: 600
      }
    });

    const dateToday = path => {
      if (path === "/movies/upcoming") {
        limit_date.value = add(new Date(), { days: 7 });
      } else if (path === "/movies/trending") {
        limit_date.value = add(new Date(), { weeks: -7 });
      } else {
        limit_date.value = null;
      }

      dispatchDate();
    };

    const dispatchDate = () => {
      let dates = {
        release_from:
          formatSelectedDate(selected.value.from) === "Invalid date"
            ? "1880-01-01"
            : formatSelectedDate(selected.value.from),
        release_to: formatSelectedDate(selected.value.to)
      };
      store.dispatch("getReleaseDate", dates);
    };

    onMounted(() => {
      dateToday(route.path);
    });

    watch(
      () => route.path,
      path => {
        dateToday(path);
      }
    );

    watch(
      () => selected.value,
      () => dispatchDate(),
      { deep: true }
    );

    watch(
      () => genre.value,
      genre => {
        let newStore = {
          ...movie_store,
          genre
        };
        localStorage.setItem("movie_store", JSON.stringify(newStore));

        // eslint-disable-next-line no-empty
        if (genre === null) {
          // eslint-disable-next-line no-prototype-builtins
          if (route.query.hasOwnProperty("genre")) {
            // eslint-disable-next-line no-unused-vars
            let { genre, ...query } = route.query;
            router.push({
              path: route.path,
              query
            });
          } else {
            router.push({
              path: route.path,
              query: route.query
            });
          }
        } else {
          router.push({
            path: route.path,
            query: {
              ...route.query,
              genre
            }
          });
        }
      }
    );

    const genresOption = ref([
      { id: null, name: "All movies" },
      {
        id: 28,
        name: "Action"
      },
      {
        id: 12,
        name: "Adventure"
      },
      {
        id: 16,
        name: "Animation"
      },
      {
        id: 35,
        name: "Comedy"
      },
      {
        id: 80,
        name: "Crime"
      },
      {
        id: 99,
        name: "Documentary"
      },
      {
        id: 18,
        name: "Drama"
      },
      {
        id: 10751,
        name: "Family"
      },
      {
        id: 14,
        name: "Fantasy"
      },
      {
        id: 36,
        name: "History"
      },
      {
        id: 27,
        name: "Horror"
      },
      {
        id: 10402,
        name: "Music"
      },
      {
        id: 9648,
        name: "Mystery"
      },
      {
        id: 10749,
        name: "Romance"
      },
      {
        id: 878,
        name: "Science Fiction"
      },
      {
        id: 10770,
        name: "TV Movie"
      },
      {
        id: 53,
        name: "Thriller"
      },
      {
        id: 10752,
        name: "War"
      },
      {
        id: 37,
        name: "Western"
      }
    ]);

    let lists = ref([
      { title: "Popular", path: "/movies", icon: "fas fa-feather-alt" },
      { title: "Trending", path: "/movies/trending", icon: "fas fa-fire-alt" },
      {
        title: "Top Rated",
        path: "/movies/top-rated",
        icon: "fas fa-star"
      },
      {
        title: "Upcoming",
        path: "/movies/upcoming",
        icon: "fas fa-drum-steelpan"
      }
    ]);

    return {
      limit_date,
      runtime,
      selected,
      genresOption,
      genre,
      lists
    };
  }
};
</script>

<style lang="scss" scoped>
.select-css {
  display: block;
  font-size: 16px;
  font-family: sans-serif;
  font-weight: 700;
  color: #444;
  line-height: 1.3;
  padding: 0.6em 1.4em 0.5em 0.8em;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0.8rem 0;
  border: 1px solid #aaa;
  box-shadow: 0 1px 0 1px rgba(0, 0, 0, 0.04);
  border-radius: 0.5em;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  cursor: pointer;
  background-color: #fff;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E"),
    linear-gradient(to bottom, #ffffff 0%, #e5e5e5 100%);
  background-repeat: no-repeat, repeat;
  background-position: right 0.7em top 50%, 0 0;
  background-size: 0.65em auto, 100%;

  &:hover {
    border-color: #888;
  }
  &:focus {
    border-color: #aaa;
    box-shadow: 0 0 1px 3px rgba(59, 153, 252, 0.7);
    box-shadow: 0 0 0 3px -moz-mac-focusring;
    color: #222;
    outline: none;
  }
}
/* Hide arrow icon in IE browsers */
.select-css::-ms-expand {
  display: none;
}
</style>
